/**
 * Smart caching utility with both in-memory and localStorage persistence.
 * 
 * Usage:
 *   cache.set('key', data, 60000);  // 1 minute TTL
 *   cache.get('key');               // returns data or null if expired
 *   cache.getOrFetch('key', fetchFn, 60000);  // cached fetch with TTL
 * 
 * For frequently-accessed data (photos, streak, protocol), use persistent cache
 * so data survives page refresh and shows instantly while fresh data loads.
 */

// ─── In-Memory Cache (fast, clears on refresh) ───────────────────────────────
class MemoryCache {
  constructor() {
    this.storage = new Map();
    this.timestamps = new Map();
  }

  set(key, value, ttl = 300000) { // default 5 minutes
    this.storage.set(key, value);
    this.timestamps.set(key, Date.now() + ttl);
  }

  get(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp || Date.now() > timestamp) {
      this.storage.delete(key);
      this.timestamps.delete(key);
      return null;
    }
    return this.storage.get(key);
  }

  has(key) {
    return this.get(key) !== null;
  }

  clear() {
    this.storage.clear();
    this.timestamps.clear();
  }

  remove(key) {
    this.storage.delete(key);
    this.timestamps.delete(key);
  }
}

// ─── Persistent Cache (survives refresh, stored in localStorage) ─────────────
const PERSISTENT_CACHE_PREFIX = 'fc_cache_';

class PersistentCache {
  set(key, value, ttl = 300000) {
    try {
      const item = {
        data: value,
        expires: Date.now() + ttl,
      };
      localStorage.setItem(PERSISTENT_CACHE_PREFIX + key, JSON.stringify(item));
    } catch (e) {
      console.warn('[Cache] localStorage write failed:', e.message);
    }
  }

  get(key) {
    try {
      const raw = localStorage.getItem(PERSISTENT_CACHE_PREFIX + key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (Date.now() > item.expires) {
        localStorage.removeItem(PERSISTENT_CACHE_PREFIX + key);
        return null;
      }
      return item.data;
    } catch (e) {
      return null;
    }
  }

  has(key) {
    return this.get(key) !== null;
  }

  remove(key) {
    localStorage.removeItem(PERSISTENT_CACHE_PREFIX + key);
  }

  clear() {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PERSISTENT_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  }
}

// ─── Unified Cache API ───────────────────────────────────────────────────────
class UnifiedCache {
  constructor() {
    this.memory = new MemoryCache();
    this.persistent = new PersistentCache();
    this.pendingFetches = new Map(); // Deduplication for concurrent requests
  }

  /**
   * Set value in both memory (fast) and localStorage (persistent).
   * Use `persistent: false` option for memory-only caching.
   */
  set(key, value, ttl = 300000, options = {}) {
    this.memory.set(key, value, ttl);
    if (options.persistent !== false) {
      this.persistent.set(key, value, ttl);
    }
  }

  /**
   * Get value from memory first, then localStorage.
   * Returns null if not found or expired.
   */
  get(key) {
    // Try memory first (faster)
    let value = this.memory.get(key);
    if (value !== null) return value;

    // Fall back to persistent storage
    value = this.persistent.get(key);
    if (value !== null) {
      // Rehydrate into memory for faster subsequent access
      this.memory.set(key, value, 60000);
    }
    return value;
  }

  has(key) {
    return this.get(key) !== null;
  }

  remove(key) {
    this.memory.remove(key);
    this.persistent.remove(key);
  }

  clear() {
    this.memory.clear();
    this.persistent.clear();
  }

  /**
   * Smart caching fetch: returns cached data if available, otherwise fetches.
   * Deduplicates concurrent requests to the same key.
   * 
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Async function that returns data
   * @param {number} ttl - Time to live in ms
   * @param {Object} options - { forceRefresh, persistent, staleWhileRevalidate }
   */
  async getOrFetch(key, fetchFn, ttl = 300000, options = {}) {
    const { forceRefresh = false, staleWhileRevalidate = false } = options;

    // Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cached = this.get(key);
      if (cached !== null) {
        // If stale-while-revalidate is enabled, trigger background refresh
        if (staleWhileRevalidate) {
          this._backgroundRefresh(key, fetchFn, ttl, options);
        }
        return cached;
      }
    }

    // Deduplicate concurrent requests
    if (this.pendingFetches.has(key)) {
      return this.pendingFetches.get(key);
    }

    // Fetch and cache
    const fetchPromise = fetchFn()
      .then(data => {
        this.set(key, data, ttl, options);
        this.pendingFetches.delete(key);
        return data;
      })
      .catch(err => {
        this.pendingFetches.delete(key);
        throw err;
      });

    this.pendingFetches.set(key, fetchPromise);
    return fetchPromise;
  }

  /**
   * Background refresh without blocking
   * @private
   */
  async _backgroundRefresh(key, fetchFn, ttl, options) {
    try {
      const data = await fetchFn();
      this.set(key, data, ttl, options);
    } catch (e) {
      // Silently fail background refresh
      console.warn(`[Cache] Background refresh failed for ${key}:`, e.message);
    }
  }
}

// Export singleton instance
export const cache = new UnifiedCache();

// Legacy export for backward compatibility
export default cache;
